import React, { useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Activity,
  Clock,
  Users,
  Briefcase,
  Download,
  LifeBuoy,
  ShieldCheck,
  TrendingUp,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Wrench,
  LayoutGrid,
} from "lucide-react";
import { toast } from "sonner";

// ---- helpers ----
const isoDaysAgo = (n) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
};
const isoToday = () => new Date().toISOString().slice(0, 10);

const fmtHours = (h) => `${(Number(h) || 0).toFixed(1)}h`;
const fmtMins = (m) => {
  const v = Number(m) || 0;
  if (v < 60) return `${v.toFixed(0)}m`;
  return `${(v / 60).toFixed(1)}h`;
};
const fmtPct = (p) => `${(Number(p) || 0).toFixed(1)}%`;

// CSV download utility. Escapes commas, quotes and newlines. Adds a UTF-8 BOM
// so Excel opens it in the correct encoding.
function downloadCsv(filename, rows) {
  if (!rows || rows.length === 0) {
    toast.error("No rows to export");
    return;
  }
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---- Small stat card ----
function StatCard({ icon: Icon, label, value, sub, tone = "blue", testid }) {
  const toneMap = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
  };
  return (
    <div
      className={`rounded-lg border p-4 flex items-start gap-3 ${toneMap[tone] || toneMap.slate}`}
      data-testid={testid}
    >
      <div className="mt-0.5">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.16em] font-mono opacity-70">{label}</div>
        <div className="mt-1 text-2xl font-heading font-bold leading-none">{value}</div>
        {sub && <div className="text-[11px] opacity-70 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

// ---- Simple horizontal bar (no external chart lib) ----
function BarRow({ label, value, max, unit = "", tone = "bg-blue-500", right }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="grid grid-cols-[10rem_1fr_5rem] items-center gap-3 text-sm">
      <div className="truncate text-slate-700">{label}</div>
      <div className="h-2 rounded bg-slate-100 overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-right font-mono text-xs text-slate-600">{right ?? `${value}${unit}`}</div>
    </div>
  );
}

// ---- Page ----
export default function TechnicalReports() {
  const [start, setStart] = useState(isoDaysAgo(30));
  const [end, setEnd] = useState(isoToday());
  const [openLongerThan, setOpenLongerThan] = useState(30);
  const [tab, setTab] = useState("overview");

  const [summary, setSummary] = useState(null);
  const [flf, setFlf] = useState(null);
  const [cap, setCap] = useState(null);
  const [tm, setTm] = useState(null);
  const [pm, setPm] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => ({ start, end }), [start, end]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, f, c, t, p] = await Promise.all([
        api.get("/reports/technical/summary", { params }),
        api.get("/reports/technical/first-line-fix", { params }),
        api.get("/reports/technical/capacity", { params }),
        api.get("/reports/technical/ticket-metrics", { params }),
        api.get("/reports/technical/project-metrics", { params: { ...params, open_longer_than_days: openLongerThan } }),
      ]);
      setSummary(s.data);
      setFlf(f.data);
      setCap(c.data);
      setTm(t.data);
      setPm(p.data);
    } catch (e) {
      toast.error("Failed to load technical reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const applyPreset = (days) => {
    setStart(isoDaysAgo(days));
    setEnd(isoToday());
  };

  return (
    <div className="space-y-6" data-testid="technical-reports-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Reporting</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Technical Reports</h1>
          <p className="text-slate-500 text-sm mt-1">
            Support & Projects analytics — first-line fix rate, capacity, ticket SLAs and delivery timing.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">From</Label>
            <Input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-40"
              data-testid="tech-report-start"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">To</Label>
            <Input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-40"
              data-testid="tech-report-end"
            />
          </div>
          <div className="flex items-center gap-1">
            {[7, 30, 90, 365].map((d) => (
              <Button
                key={d}
                variant="outline"
                size="sm"
                onClick={() => applyPreset(d)}
                data-testid={`tech-report-preset-${d}`}
              >
                {d}d
              </Button>
            ))}
          </div>
          <Button onClick={load} disabled={loading} data-testid="tech-report-run">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Run
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="tech-report-kpis">
        <StatCard
          icon={ShieldCheck}
          tone="emerald"
          label="First-line fix"
          value={fmtPct(summary?.first_line_rate)}
          sub={`${summary?.closed_tickets ?? 0} tickets closed`}
          testid="kpi-flf"
        />
        <StatCard
          icon={Clock}
          tone="blue"
          label="Avg first response"
          value={fmtHours(summary?.avg_first_response_hours)}
          sub="From ticket create to first staff reply"
          testid="kpi-frt"
        />
        <StatCard
          icon={Activity}
          tone="amber"
          label="Avg resolution"
          value={fmtHours(summary?.avg_resolution_hours)}
          sub={`${summary?.tickets_resolved ?? 0} resolved`}
          testid="kpi-res"
        />
        <StatCard
          icon={LifeBuoy}
          tone="slate"
          label="Tickets created"
          value={summary?.tickets_created ?? 0}
          sub={`vs ${summary?.tickets_resolved ?? 0} resolved`}
          testid="kpi-created"
        />
        <StatCard
          icon={Briefcase}
          tone="blue"
          label="Projects completed"
          value={summary?.projects_completed ?? 0}
          sub={`${summary?.projects_stale ?? 0} stale >30d`}
          testid="kpi-projects"
        />
        <StatCard
          icon={Users}
          tone="rose"
          label="Engineer hours"
          value={fmtHours(summary?.engineer_total_hours)}
          sub="Tickets + Projects"
          testid="kpi-engineer-hours"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="tech-report-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="flf" data-testid="tab-flf">First-line Fix</TabsTrigger>
          <TabsTrigger value="capacity" data-testid="tab-capacity">Capacity</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets">Ticket Metrics</TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-projects">Project Metrics</TabsTrigger>
        </TabsList>

        {/* -------- Overview -------- */}
        <TabsContent value="overview" className="space-y-4 pt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-heading">Top engineers by total hours</CardTitle>
              </CardHeader>
              <CardContent>
                {(summary?.top_engineers ?? []).length === 0 ? (
                  <div className="text-sm text-slate-500">No engineer activity in this window.</div>
                ) : (
                  <div className="space-y-2">
                    {(summary?.top_engineers ?? []).map((e) => {
                      const max = Math.max(...(summary?.top_engineers ?? []).map((x) => x.total_minutes || 0), 1);
                      return (
                        <BarRow
                          key={e.engineer}
                          label={e.engineer}
                          value={e.total_minutes}
                          max={max}
                          right={fmtHours(e.total_hours)}
                          tone="bg-blue-500"
                        />
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-heading">Volume snapshot</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg border p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Tickets in window</div>
                    <div className="text-2xl font-heading font-bold mt-1">{summary?.tickets_created ?? 0}</div>
                    <div className="text-slate-500 text-xs">Created</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Resolved</div>
                    <div className="text-2xl font-heading font-bold mt-1">{summary?.tickets_resolved ?? 0}</div>
                    <div className="text-slate-500 text-xs">In window</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Projects completed</div>
                    <div className="text-2xl font-heading font-bold mt-1">{summary?.projects_completed ?? 0}</div>
                    <div className="text-slate-500 text-xs">completed_at in window</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Stale open projects</div>
                    <div className="text-2xl font-heading font-bold mt-1">{summary?.projects_stale ?? 0}</div>
                    <div className="text-slate-500 text-xs">Open &gt; 30 days</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* -------- First-line fix -------- */}
        <TabsContent value="flf" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-heading">First-line fix rate by engineer</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  downloadCsv("first-line-fix.csv", flf?.rows ?? [])
                }
                disabled={!flf?.rows?.length}
                data-testid="flf-export-rows"
              >
                <Download className="h-4 w-4 mr-2" />
                Export tickets ({flf?.rows?.length ?? 0})
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-3 mb-4 text-sm">
                <div className="rounded-lg border p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Closed</div>
                  <div className="text-xl font-heading font-bold">{flf?.total_closed ?? 0}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">First-line</div>
                  <div className="text-xl font-heading font-bold text-emerald-700">{flf?.first_line ?? 0}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Escalated to vendor</div>
                  <div className="text-xl font-heading font-bold text-amber-700">{flf?.escalated ?? 0}</div>
                </div>
              </div>
              {(flf?.per_owner ?? []).length === 0 ? (
                <div className="text-sm text-slate-500">No closed tickets in this window.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                      <tr>
                        <th className="text-left py-2">Engineer</th>
                        <th className="text-right py-2">Closed</th>
                        <th className="text-right py-2">First-line</th>
                        <th className="text-right py-2">Escalated</th>
                        <th className="text-right py-2">FLF rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {flf.per_owner.map((r) => (
                        <tr key={r.owner} className="border-t" data-testid={`flf-row-${r.owner}`}>
                          <td className="py-2">{r.owner}</td>
                          <td className="py-2 text-right font-mono">{r.closed}</td>
                          <td className="py-2 text-right font-mono text-emerald-700">{r.first_line}</td>
                          <td className="py-2 text-right font-mono text-amber-700">{r.escalated}</td>
                          <td className="py-2 text-right font-mono font-semibold">{fmtPct(r.first_line_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Capacity -------- */}
        <TabsContent value="capacity" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-heading">Capacity per engineer</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadCsv("capacity.csv", cap?.engineers ?? [])}
                disabled={!cap?.engineers?.length}
                data-testid="capacity-export"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-slate-500 mb-3">
                Ticket hours from booked time on updates. Project hours from estimated minutes on completed tasks.
              </div>
              {(cap?.engineers ?? []).length === 0 ? (
                <div className="text-sm text-slate-500">No engineer activity recorded for this window.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                      <tr>
                        <th className="text-left py-2">Engineer</th>
                        <th className="text-right py-2">Ticket updates</th>
                        <th className="text-right py-2">Ticket hrs</th>
                        <th className="text-right py-2">Project tasks</th>
                        <th className="text-right py-2">Project hrs</th>
                        <th className="text-right py-2">Total hrs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cap.engineers.map((e) => (
                        <tr key={e.engineer} className="border-t" data-testid={`capacity-row-${e.engineer}`}>
                          <td className="py-2">{e.engineer}</td>
                          <td className="py-2 text-right font-mono">{e.ticket_updates}</td>
                          <td className="py-2 text-right font-mono">{fmtHours(e.ticket_hours)}</td>
                          <td className="py-2 text-right font-mono">{e.project_tasks}</td>
                          <td className="py-2 text-right font-mono">{fmtHours(e.project_hours)}</td>
                          <td className="py-2 text-right font-mono font-semibold">{fmtHours(e.total_hours)}</td>
                        </tr>
                      ))}
                      <tr className="border-t bg-slate-50 font-semibold">
                        <td className="py-2">Totals</td>
                        <td className="py-2 text-right font-mono">—</td>
                        <td className="py-2 text-right font-mono">{fmtHours((cap.totals?.ticket_minutes ?? 0) / 60)}</td>
                        <td className="py-2 text-right font-mono">—</td>
                        <td className="py-2 text-right font-mono">{fmtHours((cap.totals?.project_minutes ?? 0) / 60)}</td>
                        <td className="py-2 text-right font-mono">{fmtHours(cap.totals?.total_hours)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Ticket metrics -------- */}
        <TabsContent value="tickets" className="space-y-4 pt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-heading">Response & Resolution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg border p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Avg first response</div>
                    <div className="text-2xl font-heading font-bold mt-1">{fmtMins(tm?.avg_first_response_minutes)}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Avg resolution</div>
                    <div className="text-2xl font-heading font-bold mt-1">{fmtMins(tm?.avg_resolution_minutes)}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Created</div>
                    <div className="text-2xl font-heading font-bold mt-1">{tm?.created_in_window ?? 0}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Resolved</div>
                    <div className="text-2xl font-heading font-bold mt-1">{tm?.resolved_in_window ?? 0}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-heading">By priority (created in window)</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(tm?.priority_counts ?? {}).length === 0 ? (
                  <div className="text-sm text-slate-500">No tickets created in this window.</div>
                ) : (
                  <div className="space-y-2">
                    {(() => {
                      const items = Object.entries(tm.priority_counts);
                      const max = Math.max(...items.map(([, v]) => v), 1);
                      return items.map(([k, v]) => (
                        <BarRow
                          key={k}
                          label={k}
                          value={v}
                          max={max}
                          tone={
                            k === "Urgent"
                              ? "bg-rose-500"
                              : k === "High"
                              ? "bg-amber-500"
                              : k === "Low"
                              ? "bg-slate-400"
                              : "bg-blue-500"
                          }
                        />
                      ));
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-heading">Per engineer — avg response & resolution</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadCsv("ticket-per-owner.csv", tm?.per_owner ?? [])}
                disabled={!tm?.per_owner?.length}
                data-testid="ticket-metrics-export"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              {(tm?.per_owner ?? []).length === 0 ? (
                <div className="text-sm text-slate-500">No per-engineer data for this window.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                      <tr>
                        <th className="text-left py-2">Engineer</th>
                        <th className="text-right py-2">Responded</th>
                        <th className="text-right py-2">Avg 1st response</th>
                        <th className="text-right py-2">Resolved</th>
                        <th className="text-right py-2">Avg resolution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tm.per_owner.map((r) => (
                        <tr key={r.owner} className="border-t">
                          <td className="py-2">{r.owner}</td>
                          <td className="py-2 text-right font-mono">{r.responded_count}</td>
                          <td className="py-2 text-right font-mono">{fmtMins(r.avg_first_response_minutes)}</td>
                          <td className="py-2 text-right font-mono">{r.resolved_count}</td>
                          <td className="py-2 text-right font-mono">{fmtMins(r.avg_resolution_minutes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <TicketCategoryBreakdown tm={tm} />
        </TabsContent>

        {/* -------- Project metrics -------- */}
        <TabsContent value="projects" className="space-y-4 pt-4">
          <div className="grid md:grid-cols-3 gap-3 text-sm">
            <div className="rounded-lg border p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Closed in window</div>
              <div className="text-2xl font-heading font-bold mt-1">{pm?.closed_count ?? 0}</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Avg duration</div>
              <div className="text-2xl font-heading font-bold mt-1">{(pm?.avg_duration_days ?? 0).toFixed(1)}d</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                Engineer hours (completed steps)
              </div>
              <div className="text-2xl font-heading font-bold mt-1">
                {fmtHours(pm?.total_completed_estimated_hours)}
              </div>
            </div>
          </div>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-heading">Closed projects</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadCsv("projects-closed.csv", pm?.closed ?? [])}
                disabled={!pm?.closed?.length}
                data-testid="projects-closed-export"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              {(pm?.closed ?? []).length === 0 ? (
                <div className="text-sm text-slate-500">No projects completed in this window.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                      <tr>
                        <th className="text-left py-2">Project</th>
                        <th className="text-left py-2">Account</th>
                        <th className="text-left py-2">Owner</th>
                        <th className="text-right py-2">Completed</th>
                        <th className="text-right py-2">Duration</th>
                        <th className="text-right py-2">Engineer hrs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pm.closed.map((p) => (
                        <tr key={p.id} className="border-t" data-testid={`project-closed-row-${p.id}`}>
                          <td className="py-2 font-medium">{p.name}</td>
                          <td className="py-2 text-slate-600">{p.account_name || "—"}</td>
                          <td className="py-2 text-slate-600">{p.owner_name || "—"}</td>
                          <td className="py-2 text-right font-mono">{fmtDate(p.completed_at)}</td>
                          <td className="py-2 text-right font-mono">
                            {p.duration_days != null ? `${p.duration_days.toFixed(1)}d` : "—"}
                          </td>
                          <td className="py-2 text-right font-mono">{fmtHours(p.total_estimated_hours)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-heading flex items-center gap-3">
                Open projects older than
                <Input
                  type="number"
                  min={1}
                  className="w-20 h-7 text-sm"
                  value={openLongerThan}
                  onChange={(e) => setOpenLongerThan(Math.max(1, parseInt(e.target.value || "1", 10)))}
                  onBlur={load}
                  data-testid="open-longer-than-input"
                />
                <span>days</span>
                <TrendingUp className="h-4 w-4 text-amber-600" />
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadCsv("projects-stale.csv", pm?.open_stale ?? [])}
                disabled={!pm?.open_stale?.length}
                data-testid="projects-stale-export"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              {(pm?.open_stale ?? []).length === 0 ? (
                <div className="text-sm text-slate-500">No stale open projects — nice.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                      <tr>
                        <th className="text-left py-2">Project</th>
                        <th className="text-left py-2">Account</th>
                        <th className="text-left py-2">Owner</th>
                        <th className="text-left py-2">Status</th>
                        <th className="text-right py-2">Age</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pm.open_stale.map((p) => (
                        <tr key={p.id} className="border-t" data-testid={`project-stale-row-${p.id}`}>
                          <td className="py-2 font-medium">{p.name}</td>
                          <td className="py-2 text-slate-600">{p.account_name || "—"}</td>
                          <td className="py-2 text-slate-600">{p.owner_name || "—"}</td>
                          <td className="py-2 text-slate-600 capitalize">{(p.status || "").replace("_", " ")}</td>
                          <td className="py-2 text-right font-mono text-amber-700">
                            {p.age_days != null ? `${p.age_days.toFixed(0)}d` : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---- Ticket category breakdown (Support vs Service vs …) -----------------
function TicketCategoryBreakdown({ tm }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const rows = tm?.by_category ?? [];
  const totals = tm?.totals_all_categories;

  const toggle = (cat) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const toneForCategory = (cat) => {
    if (cat === "Service") return "bg-indigo-100 text-indigo-700 border-indigo-200";
    if (cat === "Support") return "bg-emerald-100 text-emerald-700 border-emerald-200";
    return "bg-slate-100 text-slate-700 border-slate-200";
  };

  const iconForCategory = (cat) => {
    if (cat === "Service") return <Wrench className="h-3.5 w-3.5" />;
    if (cat === "Support") return <LifeBuoy className="h-3.5 w-3.5" />;
    return <LayoutGrid className="h-3.5 w-3.5" />;
  };

  const exportRows = () =>
    rows.map((r) => ({
      category: r.category,
      created: r.created_in_window,
      closed: r.closed_in_window,
      reopened: r.reopened_in_window,
      hours: r.time_spent_hours,
      avg_time_per_closed_minutes: r.avg_time_per_closed_minutes,
      avg_1st_response_minutes: r.avg_first_response_minutes,
      median_1st_response_minutes: r.median_first_response_minutes,
      avg_resolution_minutes: r.avg_resolution_minutes,
      median_resolution_minutes: r.median_resolution_minutes,
      backlog_open: r.backlog_open,
      backlog_aged_7d: r.backlog_aged_7d,
      backlog_aged_30d: r.backlog_aged_30d,
    }));

  return (
    <Card data-testid="ticket-category-breakdown">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-sm font-heading">By category — Support vs Service (and others)</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            All tickets grouped by category. Time spent sums every event logged inside the selected window.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => downloadCsv("ticket-by-category.csv", exportRows())}
          disabled={!rows.length}
          data-testid="ticket-category-export"
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="text-sm text-slate-500">No tickets in this window.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                <tr>
                  <th className="text-left py-2 w-6"></th>
                  <th className="text-left py-2">Category</th>
                  <th className="text-right py-2">Created</th>
                  <th className="text-right py-2">Closed</th>
                  <th className="text-right py-2" title="Tickets re-opened in the window">Reopens</th>
                  <th className="text-right py-2" title="Engineer time booked against tickets in this category, within the window">Hours</th>
                  <th className="text-right py-2" title="Average time logged per ticket closed in the window">Avg / ticket</th>
                  <th className="text-right py-2">Avg 1st resp</th>
                  <th className="text-right py-2">Avg resolution</th>
                  <th className="text-right py-2" title="Open tickets as of now (approximation of end-of-window backlog)">Open</th>
                  <th className="text-right py-2" title="Open > 7 days old">≥7d</th>
                  <th className="text-right py-2" title="Open > 30 days old">≥30d</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isOpen = expanded.has(r.category);
                  return (
                    <React.Fragment key={r.category}>
                      <tr
                        className="border-t cursor-pointer hover:bg-slate-50"
                        onClick={() => toggle(r.category)}
                        data-testid={`ticket-category-row-${r.category}`}
                      >
                        <td className="py-2 text-slate-400">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="py-2">
                          <span className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-[11px] font-mono ${toneForCategory(r.category)}`}>
                            {iconForCategory(r.category)}
                            {r.category}
                          </span>
                        </td>
                        <td className="py-2 text-right font-mono">{r.created_in_window}</td>
                        <td className="py-2 text-right font-mono">{r.closed_in_window}</td>
                        <td className="py-2 text-right font-mono">{r.reopened_in_window}</td>
                        <td className="py-2 text-right font-mono font-semibold">{fmtHours(r.time_spent_hours)}</td>
                        <td className="py-2 text-right font-mono">{fmtMins(r.avg_time_per_closed_minutes)}</td>
                        <td className="py-2 text-right font-mono">{fmtMins(r.avg_first_response_minutes)}</td>
                        <td className="py-2 text-right font-mono">{fmtMins(r.avg_resolution_minutes)}</td>
                        <td className="py-2 text-right font-mono">{r.backlog_open}</td>
                        <td className="py-2 text-right font-mono">{r.backlog_aged_7d}</td>
                        <td className="py-2 text-right font-mono">{r.backlog_aged_30d}</td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t bg-slate-50/60">
                          <td colSpan={12} className="py-3 px-3">
                            <div className="grid md:grid-cols-3 gap-4">
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2">
                                  Top engineers by time
                                </div>
                                {(r.top_owners_by_time ?? []).length === 0 ? (
                                  <div className="text-xs text-slate-400">No time logged in this window.</div>
                                ) : (
                                  <ul className="space-y-1">
                                    {r.top_owners_by_time.map((o) => (
                                      <li key={o.owner} className="flex items-center justify-between text-xs">
                                        <span className="truncate">{o.owner}</span>
                                        <span className="font-mono font-semibold">{fmtHours(o.hours)}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2">
                                  Priority mix (created in window)
                                </div>
                                {Object.keys(r.priority_counts ?? {}).length === 0 ? (
                                  <div className="text-xs text-slate-400">No tickets created in this window.</div>
                                ) : (
                                  <ul className="space-y-1">
                                    {Object.entries(r.priority_counts).map(([k, v]) => (
                                      <li key={k} className="flex items-center justify-between text-xs">
                                        <span>{k}</span>
                                        <span className="font-mono">{v}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2">
                                  Response / resolution — median
                                </div>
                                <ul className="space-y-1 text-xs">
                                  <li className="flex items-center justify-between">
                                    <span>Median 1st response</span>
                                    <span className="font-mono">{fmtMins(r.median_first_response_minutes)}</span>
                                  </li>
                                  <li className="flex items-center justify-between">
                                    <span>Median resolution</span>
                                    <span className="font-mono">{fmtMins(r.median_resolution_minutes)}</span>
                                  </li>
                                  <li className="flex items-center justify-between">
                                    <span>Open ≥7d</span>
                                    <span className="font-mono">{r.backlog_aged_7d}</span>
                                  </li>
                                  <li className="flex items-center justify-between">
                                    <span>Open ≥30d</span>
                                    <span className="font-mono">{r.backlog_aged_30d}</span>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {totals && (
                  <tr className="border-t bg-slate-50 font-semibold">
                    <td className="py-2"></td>
                    <td className="py-2">Totals</td>
                    <td className="py-2 text-right font-mono">{totals.created_in_window}</td>
                    <td className="py-2 text-right font-mono">{totals.closed_in_window}</td>
                    <td className="py-2 text-right font-mono">{totals.reopened_in_window}</td>
                    <td className="py-2 text-right font-mono">{fmtHours(totals.time_spent_hours)}</td>
                    <td className="py-2 text-right font-mono">—</td>
                    <td className="py-2 text-right font-mono">{fmtMins(totals.avg_first_response_minutes)}</td>
                    <td className="py-2 text-right font-mono">{fmtMins(totals.avg_resolution_minutes)}</td>
                    <td className="py-2 text-right font-mono">{totals.backlog_open}</td>
                    <td className="py-2 text-right font-mono">{totals.backlog_aged_7d}</td>
                    <td className="py-2 text-right font-mono">{totals.backlog_aged_30d}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

